import * as htmlToImage from 'html-to-image'
import { useState } from 'react'

export type TakeScreenshotProps = {
  /**
   * Crop height in pixels.
   */
  cropHeight?: number
  /**
   * Crop width in pixels
   */
  cropWidth?: number
}

/**
 * Check if an element intersects with the viewport (any part is visible).
 * Returns false only if the element is completely outside the viewport.
 */
const isElementInViewport = (element: Element, cropHeight: number): boolean => {
  if (!element.getBoundingClientRect) {
    return true
  }
  const rect = element.getBoundingClientRect()
  // Element is completely outside if it's below the given crop height from the top (including the threshold)
  const isCompletelyOutside = rect.top > cropHeight * window.devicePixelRatio * 3
  return !isCompletelyOutside
}

export const useScreenshot = (type: string = 'image/jpeg', quality: number = 1) => {
  const [image, setImage] = useState<string | null>(null)
  const [error, setError] = useState(null)

  const takeScreenshot = (node: HTMLElement, options?: TakeScreenshotProps) => {
    if (!node) {
      throw new Error('You should provide correct html node.')
    }

    // Limit screenshot height to avoid memory issues with very tall pages
    const elementHeight = node.scrollHeight || node.offsetHeight
    const MAX_SCREENSHOT_HEIGHT = 3000

    let captureHeight = options?.cropHeight || 0
    if (!captureHeight || captureHeight > MAX_SCREENSHOT_HEIGHT) {
      captureHeight = Math.min(elementHeight, MAX_SCREENSHOT_HEIGHT)
    }

    // Combined filter function: viewport check + CORS check
    const filter = (element: Element, allowCORSImages: boolean = false) => {
      // First check if element is within viewport
      if (!isElementInViewport(element, captureHeight)) {
        return false
      }

      // Then check CORS for images
      if (element.tagName === 'IMG') {
        const img = element as HTMLImageElement

        // Allow blob URLs (they're local)
        if (img.src.startsWith('blob:')) {
          return true
        }

        // Allow data URLs
        if (img.src.startsWith('data:')) {
          return true
        }

        // Allow same origin
        if (allowCORSImages || img.src.startsWith(window.location.origin)) {
          return true
        }

        // Skip external images
        return false
      }

      return true
    }

    // using html-to-image library to take screenshot instead of html2canvas due to better quality
    // because html2canvas has issues with high-DPI displays which results in gray area in images
    return htmlToImage
      .toJpeg(node, {
        quality: quality,
        height: captureHeight,
        backgroundColor: '#ffffff',
        cacheBust: false,
        filter: (element) => filter(element, true),
      })
      .catch((error) => {
        // Retry with the same filter (already includes CORS handling)
        // eslint-disable-next-line no-console
        console.error('Error taking screenshot, retrying with CORS disabled', error)
        return htmlToImage.toJpeg(node, {
          quality: quality,
          height: captureHeight,
          backgroundColor: '#ffffff',
          cacheBust: false,
          filter: (element) => filter(element, false),
        })
      })
      .then((dataUrl) => {
        // Check if dataUrl is too large (some browsers have limits)
        const MAX_DATA_URL_SIZE = 10 * 1024 * 1024 // 10MB limit
        if (dataUrl.length > MAX_DATA_URL_SIZE) {
          throw new Error(`Screenshot data too large: ${(dataUrl.length / 1024 / 1024).toFixed(2)}MB. Try reducing the page content or height.`)
        }

        // Extract base64 directly from dataUrl
        const base64Image = dataUrl.split(',')[1]
        setImage(base64Image)
        return base64Image
      })
      .catch((error) => {
        setError(error)
        throw error
      })
  }

  return {
    image,
    takeScreenshot,
    error,
  }
}
